import { ElementRef, Injectable, inject } from '@angular/core';
import { SalesPlanFiltersPriceResponseDto, SalesPlanSeatResponseDto } from '@core/services/apiclient';
import { scaleLinear, extent } from 'd3';
import Konva from 'konva';
import { from, Observable, of, startWith, Subject, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import { CanvasProps, SelectionType } from '../models/sales-plan.model';
import { SalesPlanBlockViewInteractionService } from './sales-plan-blockview-interaction.service';
import { SalesPlanBlockViewMinimapService } from './sales-plan-blockview-minimap.service';
import { BlockviewCachePixelRatio, SeatBoxSize } from '../models/sales-plan-constants';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  applyPlanFilter as applyPlanFilterAction,
  reloadPlanView as refreshAction,
} from '@root-store/sales-plan-store/actions/sales-plan.actions';
import * as _ from 'underscore';
import { SalesBlockViewData } from '@core/models/sales-plan.model';
import { SalesPlanEventBlockViewCommunicationService } from './sales-plan-event-blockview-communication.service';
import { SalesPlanEventBlockViewUtilsService } from './sales-plan-event-blockview-utils.service';
import { SalesEventTypeEnum } from '@sales/models/sales.model';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanEventBlockViewService {
  private readonly utils = inject(SalesPlanEventBlockViewUtilsService);
  private readonly minimap = inject(SalesPlanBlockViewMinimapService);
  private readonly communication = inject(SalesPlanEventBlockViewCommunicationService);
  readonly interaction = inject(SalesPlanBlockViewInteractionService);
  private readonly store = inject<Store<fromSalesPlan.State>>(Store);
  private readonly actions = inject(Actions);

  // props
  private canvasProps: CanvasProps;
  private canvasScaleX: (position: number) => number;
  private canvasScaleY: (position: number) => number;
  private container: ElementRef;
  private minimapContainer: ElementRef;
  private topviewPlan: SVGSVGElement;
  private data: SalesBlockViewData;

  // layers & stage
  private stage: Konva.Stage;
  private seatLayer: Konva.Layer;
  private selectionLayer: Konva.Layer;
  private categoryLayer: Konva.Layer;
  private contingentLayer: Konva.Layer;

  private categoryDictionary = new Map<string, Konva.Group>();
  private contingentDictionary = new Map<string, Konva.Group>();

  public destroy$ = new Subject<void>();

  private previousPlanFilters = {
    categoryIds: [],
    contingentIds: [],
    categoryPriceIds: [],
  };

  isRefreshAction: boolean;

  /**
   * entry function to draw block view canvas
   */
  public process(
    container: ElementRef,
    minimapContainer: ElementRef,
    topviewPlan: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    seatsViewData: SalesBlockViewData,
  ) {
    this.container = container;
    this.topviewPlan = topviewPlan?.node();
    this.minimapContainer = minimapContainer;
    this.communication.clearSelection();
    this.data = seatsViewData;
    from(this.utils.init(this.data, SeatBoxSize))
      .pipe(
        switchMap(() => {
          const elements = this.utils.getAllPlanElements();
          this.canvasProps = this.utils.getCanvasProps(elements);
          this.initScalingFunctions(elements);
          this.createCanvas(container);

          return this.actions.pipe(ofType(applyPlanFilterAction, refreshAction), startWith(null)).pipe(
            tap((action) => {
              if (action !== null) {
                if (action.type === refreshAction.type) {
                  this.isRefreshAction = true;
                } else {
                  this.isRefreshAction = false;
                }
              }
            }),
            takeUntil(this.destroy$),
            withLatestFrom(
              this.store.pipe(select(fromSalesPlan.selectors.selectContingentIdsFilterSelected)),
              this.store.pipe(select(fromSalesPlan.selectors.selectCategoryIdsFilterSelected)),
              this.store.pipe(select(fromSalesPlan.selectors.selectPriceIdsFilterSelected)),
              this.store.pipe(select(fromSalesPlan.selectors.selectAllPrices)),
            ),
          );
        }),
      )
      .subscribe({
        next: ([action, contingentIds, categoryIds, priceIds, allPrices]) => {
          let categoryPriceIds = [];
          if (priceIds?.length) {
            categoryPriceIds = this.getPricesCategoryIds(priceIds, allPrices);
          }

          if (
            !_.isEqual(contingentIds, this.previousPlanFilters.contingentIds) ||
            !_.isEqual(categoryIds, this.previousPlanFilters.categoryIds) ||
            !_.isEqual(categoryPriceIds, this.previousPlanFilters.categoryPriceIds) ||
            (this.isRefreshAction && (contingentIds || categoryIds || categoryPriceIds))
          ) {
            setTimeout(() => {
              this.updateEventSeatsVisibility({ categoryIds, contingentIds, categoryPriceIds });
            }, 500);
            this.previousPlanFilters = { categoryIds, contingentIds, categoryPriceIds };
          }
        },
      });
  }

  getPricesCategoryIds(selectedPricesIds: number[], allPrices: SalesPlanFiltersPriceResponseDto[]): number[] {
    const categoryIds: number[] = [];
    selectedPricesIds.forEach((selectedId) => {
      allPrices
        .filter((price) => price.id === selectedId)
        .forEach((price) => categoryIds.push(price.categoryId));
    });
    return categoryIds;
  }
  /**
   * creates the stage and the layers
   */
  private createCanvas(canvasWrapper: ElementRef) {
    this.destroyCanvas();
    this.stage = new Konva.Stage({
      container: canvasWrapper.nativeElement,
      width: 400,
      height: 300,
      draggable: this.interaction.selectionMode === SelectionType.Drag,
      dragBoundFunc: ({ x, y }) => this.utils.defineDragBoundaries({ x, y }),
    });

    this.createLayers();
    this.cacheLayers();
    this.interaction.init(
      this.stage,
      this.container,
      this.canvasProps,
      SalesEventTypeEnum.Event,
      false,
      !!this.data.blockId,
    );
    this.render();
  }

  private createLayers() {
    this.seatLayer = new Konva.Layer({ id: 'sales-seat-layer', listening: true });
    this.selectionLayer = new Konva.Layer({ id: 'sales-selection-layer', listening: true });
    this.categoryLayer = new Konva.Layer({ id: 'sales-category-layer', listening: true });
    this.contingentLayer = new Konva.Layer({ id: 'sales-contingent-layer', listening: true });

    this.stage.add(this.seatLayer);
    this.stage.add(this.selectionLayer);
    this.stage.add(this.categoryLayer);
    this.stage.add(this.contingentLayer);
    this.initializeSeatDictionariesFromData();

    this.seatLayer.setZIndex(0);
    this.categoryLayer.setZIndex(1);
    this.contingentLayer.setZIndex(2);
    this.selectionLayer.setZIndex(3);
  }

  public initializeSeatDictionariesFromData() {
    const AllContingents = Array.from(new Set(this.data?.seats.map((seat) => seat?.seatContingent?.id)));
    const AllCategories = Array.from(new Set(this.data?.seats.map((seat) => seat?.seatCategory?.id)));

    AllContingents.forEach((contingentId) => {
      const group = new Konva.Group();
      this.contingentDictionary.set(`sales-seat-contingent-${contingentId}`, group);
      this.contingentLayer.add(group);
    });

    AllCategories.forEach((categoryId) => {
      const group = new Konva.Group();
      this.categoryDictionary.set(`sales-seat-category-${categoryId}`, group);
      this.categoryLayer.add(group);
    });
  }

  public updateEventSeatsVisibility(filters: {
    categoryIds?: number[];
    contingentIds?: number[];
    categoryPriceIds?: number[];
  }) {
    const { categoryIds, contingentIds, categoryPriceIds } = filters;
    if (
      (!categoryIds || categoryIds.length === 0) &&
      (!contingentIds || contingentIds.length === 0) &&
      (!categoryPriceIds || categoryPriceIds.length === 0)
    ) {
      this.resetFilter();
      return;
    }
    const contingentNamesSet = new Set(contingentIds.map((id) => `sales-seat-contingent-${id}`) || []);
    const categoryNamesSet = new Set(categoryIds.map((id) => `sales-seat-category-${id}`) || []);
    const categoryPriceNamesSet = new Set(categoryPriceIds.map((id) => `sales-seat-category-${id}`) || []);
    const hasCategoryPriceIds = categoryPriceIds?.length > 0;
    const hasCategories = categoryIds?.length > 0;
    const hasContingents = contingentIds?.length > 0;

    this.handleShapeProcessing(
      hasCategoryPriceIds,
      hasCategories,
      hasContingents,
      categoryPriceNamesSet,
      categoryNamesSet,
      contingentNamesSet,
    );
  }

  private getShapes(group: Konva.Group): Konva.Shape[] {
    return group.find((node) => node.getClassName() === 'Rect' || node.getClassName() === 'Circle');
  }

  private handleShapeProcessing(
    hasCategoryPriceIds: boolean,
    hasCategories: boolean,
    hasContingents: boolean,
    categoryPriceNamesSet: Set<string>,
    categoryNamesSet: Set<string>,
    contingentNamesSet: Set<string>,
  ) {
    if (hasCategories && hasContingents && hasCategoryPriceIds) {
      this.processMixedShapes(categoryPriceNamesSet, categoryNamesSet, contingentNamesSet, false);
    } else if (hasCategoryPriceIds && hasContingents) {
      this.processMixedShapes(categoryPriceNamesSet, categoryNamesSet, contingentNamesSet, true);
    } else if (hasCategoryPriceIds && hasCategories) {
      this.processCategoryShapes(categoryPriceNamesSet, categoryNamesSet, false);
    } else if (hasCategories && hasContingents) {
      this.processMixedShapes(categoryPriceNamesSet, categoryNamesSet, contingentNamesSet, false);
    } else if (hasCategories) {
      this.processCategoryShapes(categoryPriceNamesSet, categoryNamesSet, false);
    } else if (hasContingents) {
      this.processContingentShapes(contingentNamesSet);
    } else if (hasCategoryPriceIds) {
      this.processCategoryShapes(categoryPriceNamesSet, categoryNamesSet, true);
    }
  }

  processMixedShapes(
    categoryPriceNamesSet: Set<string>,
    categoryNamesSet: Set<string>,
    contingentNamesSet: Set<string>,
    isPriceFilter: boolean,
  ) {
    const nameSet = isPriceFilter ? categoryPriceNamesSet : categoryNamesSet;
    const matchedCategoryShapes = new Map<string, Konva.Shape>();
    const matchedContingentShapes = new Map<string, Konva.Shape>();

    this.categoryDictionary.forEach((categoryGroup, categoryId) => {
      categoryGroup.visible(true);
      if (nameSet.has(categoryId)) {
        const categoryShapes = this.getShapes(categoryGroup);
        categoryShapes.forEach((shape) => {
          matchedCategoryShapes.set(shape.id(), shape);
        });
      } else {
        categoryGroup.visible(false);
      }
    });

    this.contingentDictionary.forEach((contingentGroup, contingentId) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((shape) => {
        const shouldBeVisible =
          contingentNamesSet.has(contingentId) || shape.name() === 'highlighter-selected';
        shape.visible(shouldBeVisible);
        if (contingentNamesSet.has(contingentId)) {
          matchedContingentShapes.set(shape.id(), shape);
        }
      });
    });

    matchedCategoryShapes.forEach((shape, id) => {
      const isMatch = matchedContingentShapes.has(id);
      shape.visible(isMatch);
    });

    matchedContingentShapes.forEach((shape, id) => {
      const isMatch = matchedCategoryShapes.has(id);
      if (shape.name() === 'highlighter-selected') {
        shape.visible(true);
      } else {
        shape.visible(isMatch);
      }
    });
  }

  processContingentShapes(contingentNamesSet: Set<string>) {
    const visibleContingentShapeIds = new Set<string>();
    this.contingentDictionary.forEach((contingentGroup, contingentId) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((shape) => {
        const shouldBeVisible =
          contingentNamesSet.has(contingentId) || shape.name() === 'highlighter-selected';
        shape.visible(shouldBeVisible);
        if (shouldBeVisible && contingentNamesSet.has(contingentId)) {
          visibleContingentShapeIds.add(shape.id());
        }
      });
    });

    this.categoryDictionary.forEach((categoryGroup) => {
      categoryGroup.visible(true);
      const categoryShapes = this.getShapes(categoryGroup);
      categoryShapes.forEach((categoryShape) => {
        if (visibleContingentShapeIds.has(categoryShape.id())) {
          categoryShape.visible(true);
        } else {
          categoryShape.visible(false);
        }
      });
    });
  }

  processCategoryShapes(
    categoryPriceNamesSet: Set<string>,
    categoryNamesSet: Set<string>,
    isPriceFilter: boolean,
  ) {
    const visibleCategoryShapeIds = new Set<string>();
    const nameSet = isPriceFilter ? categoryPriceNamesSet : categoryNamesSet;

    this.categoryDictionary.forEach((categoryGroup, categoryId) => {
      if (nameSet.has(categoryId)) {
        categoryGroup.visible(true);
        const categoryShapes = this.getShapes(categoryGroup);
        categoryShapes.forEach((shape) => {
          shape.visible(true);
          visibleCategoryShapeIds.add(shape.id());
        });
      } else {
        categoryGroup.visible(false);
      }
    });

    this.contingentDictionary.forEach((contingentGroup) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((contingentShape: Konva.Shape) => {
        if (
          visibleCategoryShapeIds.has(contingentShape.id()) ||
          contingentShape.name() === 'highlighter-selected'
        ) {
          contingentShape.visible(true);
        } else {
          contingentShape.visible(false);
        }
      });
    });
  }

  resetFilter() {
    const makeAllShapesVisible = (group: Konva.Group) => {
      const shapes = this.getShapes(group);
      shapes.forEach((shape: Konva.Shape) => {
        shape.visible(true);
      });
    };

    this.categoryDictionary.forEach((categoryGroup) => {
      const shapes = this.getShapes(categoryGroup);
      shapes.forEach((shape: Konva.Shape) => {
        shape.visible(true);
      });
      categoryGroup.visible(true);
    });

    this.contingentDictionary.forEach((contingentGroup) => {
      makeAllShapesVisible(contingentGroup);
      contingentGroup.visible(true);
    });
  }

  /**
   * listens for actions that require the cache to be refreshed
   */
  private cacheLayers() {
    this.communication
      .getCacheRefreshSignal()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (parentGroupIds: string[]) => {
          if (this.seatLayer && !this.interaction.isPlanDrawingInProgress) {
            if (parentGroupIds?.length) {
              parentGroupIds.forEach((id) => this.utils.applyParentGroupCache(id, this.getCachePixelRatio()));
            }
          }
        },
      });
  }

  /**
   * creates and displays the plan elements: seats, letters, images  and minimap
   */
  private render() {
    this.interaction.isPlanDrawingInProgress = true;
    if (this.data?.blockId) {
      this.minimap.init(this.stage, this.minimapContainer, this.topviewPlan);
    }
    this.interaction.fitStageIntoParentContainer();
    of(this.data?.seats)
      .pipe(takeUntil(this.destroy$), this.renderSeatsAsync())
      .subscribe(() => {
        this.renderLetters();
        this.renderImages();
        if (this.data?.blockId) {
          this.minimap.cacheMinimapLayer();
        }
        Konva.autoDrawEnabled = true;
        this.interaction.addStageEventListeners();
        this.interaction.isPlanDrawingInProgress = false;
        setTimeout(() => {
          this.communication.init(this.contingentLayer, this.seatLayer);
        }, 1000);
      });
  }

  private renderSeatsAsync() {
    Konva.autoDrawEnabled = false;
    return (source$: Observable<SalesPlanSeatResponseDto[]>) => {
      return source$.pipe(
        takeUntil(this.destroy$),
        tap((seats: SalesPlanSeatResponseDto[]) => {
          const seatGroup = this.utils.createSeatParentGroup(
            seats,
            this.canvasScaleX,
            this.canvasScaleY,
            this.categoryDictionary,
            this.contingentDictionary,
            this.categoryLayer,
            this.contingentLayer,
          );
          this.seatLayer.add(seatGroup);
        }),
      );
    };
  }

  public refreshBlockViewSeats(updatedSeats: SalesPlanSeatResponseDto[]) {
    if (!updatedSeats) {
      return;
    }

    updatedSeats.forEach((changedSeat) => {
      const currentSeat = this.utils.eventData.seats.find(
        (seat) => seat.physicalSeatId == changedSeat.physicalSeatId,
      );
      const isNowSelectable = this.utils.isSeatSelectable(changedSeat);
      const isCurrentSelectable = this.utils.isSeatSelectable(currentSeat);
      const isCategoryChanged = currentSeat?.seatCategory?.id !== changedSeat?.seatCategory?.id;
      const isContingentChanged = currentSeat?.seatContingent?.id !== changedSeat?.seatContingent?.id;
      const selectableChanged = isCurrentSelectable != isNowSelectable;

      if (isNowSelectable) {
        if (isCategoryChanged || isContingentChanged) {
          this.removeSeatsFromDictionaries(currentSeat);
          this.addSeatToPlan(changedSeat);
        } else if (selectableChanged) {
          this.addSeatToPlan(changedSeat);
        }
      } else {
        let seat: SalesPlanSeatResponseDto;
        if (isCategoryChanged || isContingentChanged) {
          seat = currentSeat;
        } else {
          seat = changedSeat;
        }

        this.removeSeatsFromDictionaries(seat);
        const node = this.seatLayer.findOne(`#sales-seat-group-${seat?.physicalSeatId}`);
        if (node && node instanceof Konva.Group) {
          this.utils.appendIconToSeat(seat, node);
          this.seatLayer.batchDraw();
        }
      }
    });
    this.communication.refreshSeatSelectionNodes(updatedSeats);
    this.updateInitialData(updatedSeats);
  }

  private updateInitialData(updatedSeats: SalesPlanSeatResponseDto[]) {
    this.utils.eventData = {
      ...this.utils.eventData,
      seats: this.utils.eventData.seats.map((seat) => {
        const seatToChange = updatedSeats.find(
          (changedSeat) => changedSeat.physicalSeatId == seat.physicalSeatId,
        );
        return seatToChange || seat;
      }),
    };
  }

  private addSeatToPlan(seat: SalesPlanSeatResponseDto) {
    this.utils.createNewSeatInDictionaries(
      {
        seat,
        scaleX: this.canvasScaleX,
        scaleY: this.canvasScaleY,
      },
      this.categoryDictionary,
      this.contingentDictionary,
      this.categoryLayer,
      this.contingentLayer,
    );
    this.categoryLayer.batchDraw();
    this.contingentLayer.batchDraw();
    this.seatLayer.batchDraw();
  }

  private removeSeatsFromDictionaries(seat: SalesPlanSeatResponseDto) {
    const categoryGroup = this.categoryDictionary.get(`sales-seat-category-${seat.seatCategory?.id}`);
    if (categoryGroup) {
      const seatGroupCategory = categoryGroup.findOne(`#sales-seat-group-${seat.physicalSeatId}`);
      if (seatGroupCategory) {
        seatGroupCategory.destroy();
      }
    }

    const contingentGroup = this.contingentDictionary.get(`sales-seat-contingent-${seat.seatContingent?.id}`);
    if (contingentGroup) {
      const seatGroupContingent = contingentGroup.findOne(`#sales-seat-group-${seat.physicalSeatId}`);
      if (seatGroupContingent) {
        seatGroupContingent.destroy();
      }
    }
  }

  /**
   * adds letters to canvas
   */
  private renderLetters() {
    if (this.data.letters?.length) {
      this.data.letters.forEach((letter) => {
        const letterElement = this.utils.drawLetter(letter, this.canvasScaleX, this.canvasScaleY);
        this.seatLayer.add(letterElement);
        letterElement.moveToBottom();
      });
    }
  }
  /**
   * adds images to canvas
   */
  private renderImages() {
    if (this.data.imageData) {
      this.data.imageData?.imageDatas?.forEach((imageData) => {
        const imageContent = this.data.imageData?.imageContents.find(
          (content) => imageData?.imageContentId === content?.imageContentId,
        );
        const image = new Image();
        const extension = imageContent?.extension === 'UNKNOWN' ? 'png' : imageContent.extension;
        image.src = `data:image/${extension};base64,${imageContent?.image}`;
        image.onload = () => {
          const imageElement = this.utils.drawImage(imageData, image, this.canvasScaleX, this.canvasScaleY);
          this.seatLayer.add(imageElement);
          imageElement.moveToBottom();
        };
      });
    }
  }
  /**
   * @returns an observable with cursor position and seatId in order to get the seat details
   */
  public requestSeatDetails() {
    return this.interaction.seatDetailsRequested$.asObservable();
  }

  /**
   *
   * @returns an observable to check if loader is required when rendering the plan
   */
  public isLoaderDisplayed() {
    return this.utils.progressInfo();
  }

  private getCachePixelRatio() {
    return this.interaction.maxScaleValue > BlockviewCachePixelRatio
      ? BlockviewCachePixelRatio
      : this.interaction.maxScaleValue;
  }

  /**
   * prepares the two scaling functions for x and y. These functions will help to keep our seats within the container
   * even if the container is smaller/bigger than the natural width/height of our plan
   */
  private initScalingFunctions(elements: { x: number; y: number; [key: string]: any }[]) {
    const scale = this.utils.getInitialScale(this.container, this.canvasProps);
    const widthTranslateRatio =
      ((this.container?.nativeElement?.offsetWidth - 8) / scale - this.canvasProps?.width) / 2;
    this.canvasScaleX = scaleLinear()
      .domain(extent(elements, (element) => element?.x))
      .range([widthTranslateRatio, widthTranslateRatio + this.canvasProps?.width]);

    this.canvasScaleY = scaleLinear()
      .domain(extent(elements, (element) => element?.y))
      .range([0, 0 + this.canvasProps?.height]);
  }

  public destroyCanvas() {
    if (this.stage) {
      this.stage.destroy();
      this.categoryDictionary.clear();
      this.contingentDictionary.clear();
      this.minimap.destroyMinimap();
      this.destroy$.next();
      this.destroy$.complete(); // TS ACA ajouter après le next pour notifier que l'observable est terminé
      this.interaction.destroy();
      this.communication.destroy();
      this.previousPlanFilters = {
        categoryIds: [],
        contingentIds: [],
        categoryPriceIds: [],
      };
    }
  }
}
